import { z } from 'zod';
/**
 * SLA and breakdown analytics (SPEC.md §5.6).
 *
 * The calculators are pure so the same numbers can be produced on the server for a
 * report and on the client for a live tile, without two implementations drifting.
 */
const MINUTE_MS = 60_000;
/**
 * §5.6 sets response time under 5 minutes as the target.
 *
 * Deliberately independent of the SLA policy (Q-TARGET-TILE). This drives the
 * dashboard's hit-rate tile and measures response across ALL priorities; the
 * URGENT ack window is a different number that happens to read 5 today. Coupling
 * them would silently rewrite hit-rate history every time the stored windows are
 * retuned — a metric that moves under its own past is not a metric. If the policy
 * ever drifts far from this, see DEBT-14 before changing either.
 */
export const RESPONSE_TIME_TARGET_MINUTES = 5;
/** Minutes from report to acknowledgement. Null while still unacknowledged. */
export function responseTimeMinutes(ticket) {
    if (!ticket.acknowledgedAt)
        return null;
    return Math.max(0, (ticket.acknowledgedAt.getTime() - ticket.createdAt.getTime()) / MINUTE_MS);
}
/**
 * Minutes from report to resolution, **excluding time on hold**.
 *
 * Decision D6 is explicit that on-hold time must not count against the team: three
 * days waiting on a vendor's pump seal is not three days of repair. Including it
 * would make MTTR a measure of supplier lead times rather than of the team.
 */
export function repairTimeMinutes(ticket) {
    if (!ticket.resolvedAt)
        return null;
    const elapsed = (ticket.resolvedAt.getTime() - ticket.createdAt.getTime()) / MINUTE_MS;
    return Math.max(0, elapsed - ticket.totalHoldDurationMins);
}
/** Mean of the defined values; null when nothing qualifies, never NaN. */
export function mean(values) {
    const defined = values.filter((value) => value !== null);
    if (defined.length === 0)
        return null;
    return defined.reduce((total, value) => total + value, 0) / defined.length;
}
export const slaSummarySchema = z.object({
    /** Tickets opened in the window. */
    ticketsOpened: z.number().int().nonnegative(),
    ticketsResolved: z.number().int().nonnegative(),
    /** Still consuming attention at the end of the window. */
    ticketsOpen: z.number().int().nonnegative(),
    /** Mean minutes to acknowledgement, or null when none were acknowledged. */
    averageResponseMinutes: z.number().nullable(),
    /** Mean minutes to repair, excluding on-hold time (D6). */
    meanTimeToRepairMinutes: z.number().nullable(),
    /** Tickets whose acknowledgement SLA was missed (D15). */
    ackBreaches: z.number().int().nonnegative(),
    /**
     * Tickets whose resolution deadline passed while the work was still live
     * (Q-RESOLVE-BREACH). Counted from the sweep's stamp, so a ticket resolved
     * late still counts — the breach happened, however it ended.
     */
    resolveBreaches: z.number().int().nonnegative(),
    /** Share of acknowledged tickets that met the response target, 0–1. */
    responseTargetHitRate: z.number().min(0).max(1).nullable(),
    /** Share of resolved tickets that carried a proof photo, 0–1 (D2 — a metric, not a gate). */
    proofCoverage: z.number().min(0).max(1).nullable(),
});
export const assetFailureRowSchema = z.object({
    assetId: z.string(),
    assetName: z.string(),
    category: z.string(),
    locationName: z.string().nullable(),
    failureCount: z.number().int().nonnegative(),
    /** Parts consumed against this asset, in minor units. */
    partsCostMinor: z.number().int().nonnegative(),
});
export const costlyLocationRowSchema = z.object({
    locationId: z.string(),
    locationName: z.string(),
    ticketCount: z.number().int().nonnegative(),
    partsCostMinor: z.number().int().nonnegative(),
});
export const analyticsOverviewSchema = z.object({
    windowDays: z.number().int().positive(),
    generatedAt: z.string(),
    /**
     * The reporting baseline in force when these figures were computed, or null when
     * the full history counts. Sent with the figures so the dashboard can state the
     * period they cover — a number that silently excludes history is a lie told by
     * omission, and the heading is where it stops being silent.
     */
    analyticsBaselineAt: z.string().nullable(),
    sla: slaSummarySchema,
    repeatFailures: z.array(assetFailureRowSchema),
    costlyLocations: z.array(costlyLocationRowSchema),
});
/**
 * The reporting baseline itself, as the root tier reads and writes it: one
 * property-wide date the dashboard counts from, or null for the full history.
 *
 * A lens, never a rewrite. Setting it must change no ticket row — `ackBreachedAt`
 * doubles as the escalation sweep's idempotency guard, and clearing it would
 * re-escalate weeks-old faults within a minute — and must leave the queue, the
 * exports and the audit trail showing everything. Property-wide on purpose: the
 * figures are a shared operational fact, and two managers reading "Mean repair
 * time" over different periods is how a number stops being trusted (DEBT-18 holds
 * the personal date-range picker this is not).
 */
export const analyticsBaselineSchema = z.object({
    analyticsBaselineAt: z.string().datetime().nullable(),
});
/** Builds the SLA block from raw ticket timings. Pure, so it is directly testable. */
export function summariseSla(tickets, openCount) {
    const acknowledged = tickets.filter((ticket) => ticket.acknowledgedAt !== null);
    const resolved = tickets.filter((ticket) => ticket.resolvedAt !== null);
    const withinTarget = acknowledged.filter((ticket) => {
        const response = responseTimeMinutes(ticket);
        return response !== null && response <= RESPONSE_TIME_TARGET_MINUTES;
    });
    return {
        ticketsOpened: tickets.length,
        ticketsResolved: resolved.length,
        ticketsOpen: openCount,
        averageResponseMinutes: mean(tickets.map(responseTimeMinutes)),
        meanTimeToRepairMinutes: mean(tickets.map(repairTimeMinutes)),
        ackBreaches: tickets.filter((ticket) => ticket.ackBreached).length,
        resolveBreaches: tickets.filter((ticket) => ticket.resolveBreached).length,
        responseTargetHitRate: acknowledged.length === 0 ? null : withinTarget.length / acknowledged.length,
        proofCoverage: resolved.length === 0
            ? null
            : resolved.filter((ticket) => ticket.hasProofPhoto).length / resolved.length,
    };
}
//# sourceMappingURL=metrics.js.map